import { cn } from "@/lib/utils";

interface PanelLayoutProps {
  /** Navigation items rendered in the left sidebar */
  sidebar: React.ReactNode;
  /** Main content — rendered centered inside the scrollable right area */
  children: React.ReactNode;
  /** Optional extra className for the content wrapper */
  contentClassName?: string;
  /** Optional extra className for the sidebar wrapper (overrides width/padding) */
  sidebarClassName?: string;
  /**
   * When true, renders children directly in a flex-col overflow-hidden container
   * instead of the default centered/scrollable wrapper. Use for master-detail layouts.
   */
  rawContent?: boolean;
}

/**
 * Two-column admin panel layout:
 *   - Desktop: fixed-width (w-52) left sidebar with a right border, content beside it
 *   - Mobile: the sidebar collapses to a horizontal top bar (border-b) and the
 *     content stacks below it full-width
 *   - Right: scrollable area with a centered, max-width-constrained content column
 *
 * Pass rawContent=true for master-detail layouts where the right pane manages
 * its own scrolling (e.g. a chat viewer).
 */
export function PanelLayout({ sidebar, children, contentClassName, sidebarClassName, rawContent }: PanelLayoutProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
      {/* Sidebar — top bar on mobile, left column on desktop */}
      <div
        className={cn(
          "flex min-h-0 shrink-0 flex-col overflow-y-auto border-b border-border p-3 max-md:max-h-44 md:w-52 md:border-r md:border-b-0",
          sidebarClassName
        )}
      >
        {sidebar}
      </div>

      {/* Content */}
      {rawContent ? (
        <div className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", contentClassName)}>
          {children}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className={cn("mx-auto max-w-2xl space-y-8 px-4 py-6 sm:px-10 sm:py-10", contentClassName)}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
}
